import React, { useEffect, useMemo, useState } from "react";
import { resolveCoverUrl } from "../utils/coverUrl";

/**
 * A stable, accessible presentation for book artwork wherever a real content
 * record is shown. It intentionally accepts only the cover fields already
 * returned by the content APIs: `cover_image` and `cover_image_url`.
 *
 * The wrapper owns the image failure state so a bad upload never leaves a
 * broken-image icon or changes the surrounding card's dimensions.
 */
const BookCover = ({
  book,
  cover,
  title,
  author,
  className = "",
  imageClassName = "",
  priority = false,
}) => {
  const rawCover =
    typeof cover === "string"
      ? cover
      : book?.cover_image || book?.cover_image_url || "";
  const source = useMemo(() => resolveCoverUrl(rawCover), [rawCover]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [source]);

  const displayTitle =
    title || book?.title || book?.series_title || "No cover available";
  const displayAuthor = author || book?.author || book?.author_name || "";
  const alt = displayAuthor
    ? `Cover of ${displayTitle} by ${displayAuthor}`
    : `Cover of ${displayTitle}`;
  const fallbackLabel =
    displayTitle === "No cover available"
      ? "No cover available"
      : `No cover available for ${displayTitle}`;

  return (
    <div className={`relative overflow-hidden bg-slate-900 ${className}`}>
      {source && !failed ? (
        <img
          src={source}
          alt={alt}
          loading={priority ? "eager" : "lazy"}
          className={`h-full w-full object-cover ${imageClassName}`}
          onError={() => setFailed(true)}
        />
      ) : (
        <div
          className="relative flex h-full w-full flex-col justify-between overflow-hidden bg-gradient-to-br from-slate-800 via-slate-900 to-indigo-950 p-3 text-left text-white"
          role="img"
          aria-label={fallbackLabel}
        >
          <div
            className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-amber-300 via-violet-400 to-cyan-300"
            aria-hidden="true"
          />
          <span className="mt-1 text-[8px] font-bold uppercase tracking-[0.22em] text-white/45">
            {displayTitle === "No cover available" ? "Library" : "Edition"}
          </span>
          <div className="min-w-0">
            <p className="font-display text-sm font-black leading-tight text-white line-clamp-4">
              {displayTitle}
            </p>
            {displayAuthor && (
              <p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-white/60 line-clamp-2">
                {displayAuthor}
              </p>
            )}
            {displayTitle === "No cover available" && (
              <p className="mt-1 text-[9px] text-white/55">
                No cover available
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookCover;
